
import { Canvas, Circle, Ellipse, Group, Line, Polygon, Rect, Shadow, Text } from "fabric";
import { CustomFabricObject, CustomGroup, CustomCanvas } from "@/types/fabric";
import { pixelsToMillimeters } from "@/utils/measurement-utils";
import { MeasurementPoint } from "@/types/measurements";

// Centralized styling constants for consistent appearance
export const MEASUREMENT_STYLES = {
  label: {
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    textColor: '#8B5CF6',
    fontSize: 10,
    padding: 2,
    borderRadius: 4,
    shadow: {
      color: 'rgba(139, 92, 246, 0.15)',
      blur: 6,
      offsetX: 0,
      offsetY: 2
    }
  },
  point: {
    radius: 4,
    fillColor: '#8B5CF6',
    strokeColor: '#ffffff',
    strokeWidth: 1.5,
    shadow: {
      color: 'rgba(139, 92, 246, 0.4)',
      blur: 10,
      offsetX: 0,
      offsetY: 0
    }
  },
  line: {
    foreground: {
      strokeColor: '#8B5CF6',
      strokeWidth: 2,
      strokeLineCap: 'round' as CanvasLineCap,
      strokeLineJoin: 'round' as CanvasLineJoin
    },
    background: {
      strokeColor: '#ffffff',
      strokeWidth: 4,
      strokeLineCap: 'round' as CanvasLineCap,
      strokeLineJoin: 'round' as CanvasLineJoin,
      shadow: {
        color: 'rgba(0, 0, 0, 0.15)',
        blur: 4,
        offsetX: 0,
        offsetY: 1
      }
    }
  }
};

export class MeasurementService {
  // Create text for measurement labels
  static createMeasurementText(content: string, zoom: number = 1): Text {
    return new Text(content, {
      fontSize: MEASUREMENT_STYLES.label.fontSize,
      fill: MEASUREMENT_STYLES.label.textColor,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: 600,
      textAlign: 'center',
      scaleX: 1 / zoom,
      scaleY: 1 / zoom,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
  }

  // Create background for measurement labels
  static createLabelBackground(text: Text, zoom: number = 1): Rect {
    const padding = MEASUREMENT_STYLES.label.padding / zoom;
    const width = text.width! + (padding * 3);
    const height = text.height! + (padding * 2);
    
    return new Rect({
      width: width,
      height: height,
      fill: MEASUREMENT_STYLES.label.backgroundColor,
      rx: MEASUREMENT_STYLES.label.borderRadius / zoom,
      ry: MEASUREMENT_STYLES.label.borderRadius / zoom,
      stroke: 'rgba(139, 92, 246, 0.2)',
      strokeWidth: 1.5 / zoom,
      scaleX: 1 / zoom,
      scaleY: 1 / zoom,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      shadow: new Shadow({
        color: MEASUREMENT_STYLES.label.shadow.color,
        blur: MEASUREMENT_STYLES.label.shadow.blur / zoom,
        offsetX: MEASUREMENT_STYLES.label.shadow.offsetX,
        offsetY: MEASUREMENT_STYLES.label.shadow.offsetY / zoom
      })
    });
  }

  // Create a complete measurement label (text + background)
  static createMeasurementLabel(
    content: string,
    x: number,
    y: number,
    angle: number = 0,
    isPermanent: boolean = false,
    zoom: number = 1,
    type: 'measurement' | 'rulerMeasurement' | 'angleMeasurement' = 'measurement',
    measurementId?: string
  ): Group {
    const text = this.createMeasurementText(content, zoom);
    const background = this.createLabelBackground(text, zoom);
    
    const group = new Group([background, text], {
      left: x,
      top: y,
      angle: angle,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      subTargetCheck: false,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    });
    
    ((group as unknown) as CustomFabricObject).data = {
      type,
      isPermanent,
      ...(measurementId ? { measurementId } : {})
    };
    
    return group;
  }

  // Update text content in existing labels
  static updateMeasurementText(label: Text | Group, content: string): void {
    if (label instanceof Group) {
      const text = label.getObjects().find(obj => obj instanceof Text) as Text;
      if (text) {
        text.set({ text: content });
      }
    } else if (label instanceof Text) {
      label.set({ text: content });
    }
  }

  // Create point markers for measurements
  static createPointMarker(
    x: number, 
    y: number, 
    zoom: number = 1, 
    isPermanent: boolean = false,
    type: 'measurement' | 'rulerMeasurement' | 'angleMeasurement' = 'measurement',
    measurementId?: string
  ): Circle {
    const circle = new Circle({
      left: x - (MEASUREMENT_STYLES.point.radius / zoom),
      top: y - (MEASUREMENT_STYLES.point.radius / zoom),
      radius: MEASUREMENT_STYLES.point.radius / zoom,
      fill: MEASUREMENT_STYLES.point.fillColor,
      stroke: MEASUREMENT_STYLES.point.strokeColor,
      strokeWidth: MEASUREMENT_STYLES.point.strokeWidth / zoom,
      selectable: false,
      evented: false,
      shadow: new Shadow({
        color: MEASUREMENT_STYLES.point.shadow.color,
        blur: MEASUREMENT_STYLES.point.shadow.blur / zoom,
        offsetX: MEASUREMENT_STYLES.point.shadow.offsetX,
        offsetY: MEASUREMENT_STYLES.point.shadow.offsetY / zoom
      }),
      opacity: 0.9,
      noScaleCache: true
    });

    ((circle as unknown) as CustomFabricObject).data = {
      type,
      isPermanent,
      ...(measurementId ? { measurementId } : {})
    };

    return circle;
  }

  // Create measurement lines (background + foreground for contrast)
  static createMeasurementLine(
    points: [number, number, number, number],
    zoom: number = 1,
    isPermanent: boolean = false,
    type: 'measurement' | 'rulerMeasurement' | 'angleMeasurement' = 'measurement',
    measurementId?: string
  ): Line[] {
    // Create background line (white with shadow)
    const backgroundLine = new Line(points, {
      stroke: MEASUREMENT_STYLES.line.background.strokeColor,
      strokeWidth: MEASUREMENT_STYLES.line.background.strokeWidth / zoom,
      strokeLineCap: MEASUREMENT_STYLES.line.background.strokeLineCap,
      strokeLineJoin: MEASUREMENT_STYLES.line.background.strokeLineJoin,
      selectable: false,
      evented: false,
      shadow: new Shadow({
        color: MEASUREMENT_STYLES.line.background.shadow.color,
        blur: MEASUREMENT_STYLES.line.background.shadow.blur / zoom,
        offsetX: MEASUREMENT_STYLES.line.background.shadow.offsetX,
        offsetY: MEASUREMENT_STYLES.line.background.shadow.offsetY / zoom
      })
    });

    // Create foreground line (colored)
    const foregroundLine = new Line(points, {
      stroke: MEASUREMENT_STYLES.line.foreground.strokeColor,
      strokeWidth: MEASUREMENT_STYLES.line.foreground.strokeWidth / zoom,
      strokeLineCap: MEASUREMENT_STYLES.line.foreground.strokeLineCap,
      strokeLineJoin: MEASUREMENT_STYLES.line.foreground.strokeLineJoin,
      selectable: false,
      evented: false
    });

    // Set data attributes for both lines
    [backgroundLine, foregroundLine].forEach(line => {
      ((line as unknown) as CustomFabricObject).data = {
        type,
        isPermanent,
        ...(measurementId ? { measurementId } : {})
      };
    });

    return [backgroundLine, foregroundLine];
  }

  // Create a measurement group that includes a shape and its measurements
  static createMeasurementGroup(
    shape: any, 
    measurements: any[], 
    canvas: Canvas,
    shapeType: string
  ): CustomGroup {
    // Ensure measurements are marked as permanent
    measurements.forEach(measurement => {
      const customMeasurement = measurement as unknown as CustomFabricObject;
      
      if (!customMeasurement.data?.isPermanent) {
        customMeasurement.data = {
          ...(customMeasurement.data || {}),
          type: 'measurement' as 'measurement' | 'rulerMeasurement' | 'angleMeasurement',
          isPermanent: true
        };
      }
      
      // Set visibility based on canvas showMeasurements setting
      const customCanvas = canvas as CustomCanvas;
      if (customCanvas.data && customCanvas.data.showMeasurements === false) {
        measurement.visible = false;
      }
    });

    // Create the group
    const group = new Group([shape, ...measurements], {
      selectable: true,
      hasControls: true,
      subTargetCheck: false,
      interactive: true,
      lockScalingFlip: true,
    });

    const customGroup = group as unknown as CustomGroup;
    
    // Use shape name or generate a default name
    const shapeName = (shape as CustomFabricObject).data?.name || 
                     `${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)} Template`;
    
    // Set group data
    customGroup.data = {
      type: 'incision',
      shapeType: shapeType as any,
      name: shapeName,
      isMeasurementGroup: true
    };

    canvas.add(group);

    // Scale handler
    group.on('scaling', () => {
      const obj = shape as any;
      if (obj.width && obj.height) {
        obj.width *= obj.scaleX || 1;
        obj.height *= obj.scaleY || 1;
        obj.scaleX = 1;
        obj.scaleY = 1;
      } else if (obj.radius) {
        const customObj = obj as CustomFabricObject;
        const isOval = customObj.data?.isOval;
        
        if (isOval) {
          const avgScale = (group.scaleX! + group.scaleY!) / 2;
          
          obj.radius *= avgScale;
          
          obj.scaleX = group.scaleX! / avgScale;
          obj.scaleY = group.scaleY! / avgScale;
          
          group.scaleX = 1;
          group.scaleY = 1;
        } else {
          obj.radius *= group.scaleX || 1;
          obj.scaleX = 1;
          obj.scaleY = 1;
        }
      }
      
      this.updateShapeMeasurements(customGroup, canvas);
    });

    // Mouse event handlers
    group.on('mousedown', () => {
      customGroup.lockScalingX = false;
      customGroup.lockScalingY = false;
      customGroup.setControlsVisibility({
        mb: false,
        mt: false,
        ml: false,
        mr: false
      });
    });

    group.on('modified', () => {
      this.updateShapeMeasurements(customGroup, canvas);
    });

    return customGroup;
  }

  // Update measurements after shape modifications
  static updateShapeMeasurements(group: CustomGroup, canvas: Canvas): void {
    const objects = group.getObjects();
    const shape = objects[0];
    const measurements = objects.slice(1);
    const shapeType = group.data?.shapeType;

    // Select appropriate update method based on shape type
    if (shape instanceof Rect) {
      this.updateRectangleMeasurements(shape, measurements, canvas);
    } else if (shape instanceof Circle || shape instanceof Ellipse) {
      this.updateCircleMeasurements(shape, measurements, canvas);
    } else if (shape instanceof Polygon) {
      if (shapeType === 'rhomboid') {
        this.updateRhomboidMeasurements(shape, measurements, canvas);
      } else {
        this.updatePolygonMeasurements(shape, measurements, canvas);
      }
    } else if (shape instanceof Line) {
      this.updateLineMeasurements(shape, measurements, canvas);
    }

    const zoom = canvas.getZoom();
    this.updateMeasurementScaling(measurements, zoom);
    
    canvas.renderAll();
  }

  // Update all measurement objects for the current zoom level
  static updateMeasurementScaling(measurements: any[], zoom: number): void {
    measurements.forEach(label => {
      if (label instanceof Group) {
        label.getObjects().forEach(obj => {
          if (obj instanceof Text) {
            obj.set({
              scaleX: 1 / zoom,
              scaleY: 1 / zoom
            });
          } else if (obj instanceof Rect) {
            obj.set({
              scaleX: 1 / zoom,
              scaleY: 1 / zoom,
              rx: MEASUREMENT_STYLES.label.borderRadius / zoom,
              ry: MEASUREMENT_STYLES.label.borderRadius / zoom,
              strokeWidth: 1.5 / zoom,
              shadow: new Shadow({
                color: MEASUREMENT_STYLES.label.shadow.color,
                blur: MEASUREMENT_STYLES.label.shadow.blur / zoom,
                offsetX: MEASUREMENT_STYLES.label.shadow.offsetX,
                offsetY: MEASUREMENT_STYLES.label.shadow.offsetY / zoom
              })
            });
          }
        });
      } else if (label instanceof Circle) {
        label.set({
          radius: MEASUREMENT_STYLES.point.radius / zoom,
          strokeWidth: MEASUREMENT_STYLES.point.strokeWidth / zoom
        });
      } else if (label instanceof Line) {
        const isBackground = label.stroke === MEASUREMENT_STYLES.line.background.strokeColor;
        label.set({
          strokeWidth: isBackground 
            ? MEASUREMENT_STYLES.line.background.strokeWidth / zoom 
            : MEASUREMENT_STYLES.line.foreground.strokeWidth / zoom
        });
      }
    });
  }

  // Update all measurements on the canvas for a given zoom level
  static updateAllMeasurements(canvas: Canvas, zoom: number): void {
    if (!canvas) return;
    
    canvas.getObjects().forEach(obj => {
      const customObj = obj as CustomFabricObject | CustomGroup;
      
      if (customObj.data?.isMeasurementGroup) {
        // For measurement groups, update all child measurements
        const measurementLabels = (obj as Group).getObjects().filter(child => {
          const childObj = child as CustomFabricObject;
          return childObj.data?.type === 'measurement' || 
                 childObj.data?.type === 'rulerMeasurement' || 
                 childObj.data?.type === 'angleMeasurement';
        });
        
        this.updateMeasurementScaling(measurementLabels, zoom);
      } else if (customObj.data?.type === 'measurement' || 
                customObj.data?.type === 'rulerMeasurement' ||
                customObj.data?.type === 'angleMeasurement') {
        // For standalone measurement objects
        if (obj instanceof Group) {
          this.updateMeasurementScaling([obj], zoom);
        }
      }
    });
    
    canvas.renderAll();
  }

  // Rectangle measurement updates
  static updateRectangleMeasurements(rect: Rect, labels: any[], canvas: Canvas): void {
    if (!rect || labels.length < 2) return;

    const [widthLabel, heightLabel] = labels;
    
    const parent = rect.group;
    const parentScaleX = parent ? (parent.scaleX || 1) : 1;
    const parentScaleY = parent ? (parent.scaleY || 1) : 1;

    const width = rect.width! * parentScaleX;
    const height = rect.height! * parentScaleY;

    const widthMm = Math.round(pixelsToMillimeters(width, canvas));
    const heightMm = Math.round(pixelsToMillimeters(height, canvas));

    this.updateMeasurementText(widthLabel, `${widthMm}mm`);
    this.updateMeasurementText(heightLabel, `${heightMm}mm`);
  }

  // Circle/Ellipse measurement updates
  static updateCircleMeasurements(shape: Circle | Ellipse, labels: any[], canvas: Canvas): void {
    if (!shape || !labels.length) return;
    
    const label = labels[0];
    const parent = shape.group;
    const parentScale = parent ? (parent.scaleX || 1) : 1;
    
    let diameter;
    
    if (shape instanceof Circle) {
      diameter = shape.radius! * 2 * parentScale;
    } else {
      const scaleX = parent ? (parent.scaleX || 1) : 1;
      const scaleY = parent ? (parent.scaleY || 1) : 1;
      const rx = (shape.rx || 0) * scaleX;
      const ry = (shape.ry || 0) * scaleY;
      
      diameter = (rx + ry);
    }
    
    const diameterMm = Math.round(pixelsToMillimeters(diameter, canvas));
    this.updateMeasurementText(label, `∅${diameterMm}mm`);
  }

  // Line measurement updates
  static updateLineMeasurements(line: Line, labels: any[], canvas: Canvas): void {
    if (!line || !labels.length) return;
    
    const label = labels[0];
    
    const parent = line.group;
    const parentScaleX = parent ? (parent.scaleX || 1) : 1;
    const parentScaleY = parent ? (parent.scaleY || 1) : 1;

    const dx = (line.x2! - line.x1!) * parentScaleX;
    const dy = (line.y2! - line.y1!) * parentScaleY;
    const length = Math.sqrt(dx * dx + dy * dy);
    const lengthMm = Math.round(pixelsToMillimeters(length, canvas));
    
    this.updateMeasurementText(label, `${lengthMm}mm`);

    if (parent && label instanceof Group) {
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      label.set({ angle: angle });
    }
  }

  // Polygon measurement updates
  static updatePolygonMeasurements(polygon: Polygon, labels: any[], canvas: Canvas): void {
    if (!polygon) return;

    const parent = polygon.group;
    const parentScaleX = parent ? (parent.scaleX || 1) : 1;
    const parentScaleY = parent ? (parent.scaleY || 1) : 1;
    
    const points = polygon.points || [];
    
    for (let i = 0; i < points.length && i < labels.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      
      const x1 = p1.x * parentScaleX;
      const y1 = p1.y * parentScaleY;
      const x2 = p2.x * parentScaleX;
      const y2 = p2.y * parentScaleY;
      
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx * dx + dy * dy);
      const lengthMm = Math.round(pixelsToMillimeters(length, canvas));
      
      if (labels[i]) {
        this.updateMeasurementText(labels[i], `${lengthMm}mm`);
        
        if (labels[i] instanceof Group) {
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          labels[i].set({ angle: angle });
        }
      }
    }
  }

  // Rhomboid measurement updates
  static updateRhomboidMeasurements(polygon: Polygon, labels: any[], canvas: Canvas): void {
    if (!polygon || labels.length !== 2) return;
    
    const parent = polygon.group;
    const parentScaleX = parent ? (parent.scaleX || 1) : 1;
    const parentScaleY = parent ? (parent.scaleY || 1) : 1;
    
    const points = polygon.points || [];
    if (points.length !== 4) return;
    
    const [widthLabel, heightLabel] = labels;
    
    const topP1 = points[0];
    const topP2 = points[1];
    const widthDx = (topP2.x - topP1.x) * parentScaleX;
    const widthDy = (topP2.y - topP1.y) * parentScaleY;
    const width = Math.sqrt(widthDx * widthDx + widthDy * widthDy);
    const widthMm = Math.round(pixelsToMillimeters(width, canvas));
    
    const leftP1 = points[3];
    const leftP2 = points[0];
    const heightDx = (leftP2.x - leftP1.x) * parentScaleX;
    const heightDy = (leftP2.y - leftP1.y) * parentScaleY;
    const height = Math.sqrt(heightDx * heightDx + heightDy * heightDy);
    const heightMm = Math.round(pixelsToMillimeters(height, canvas));
    
    this.updateMeasurementText(widthLabel, `${widthMm}mm`);
    this.updateMeasurementText(heightLabel, `${heightMm}mm`);
    
    const topAngle = Math.atan2(widthDy, widthDx) * (180 / Math.PI);
    const leftAngle = Math.atan2(heightDy, heightDx) * (180 / Math.PI);
    
    if (widthLabel instanceof Group) {
      widthLabel.set({ angle: topAngle });
    }
    
    if (heightLabel instanceof Group) {
      heightLabel.set({ angle: leftAngle });
    }
  }

  // Calculate distance between two points
  static calculateDistance(p1: MeasurementPoint, p2: MeasurementPoint): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Calculate angle formed by three points
  static calculateAngle(p1: MeasurementPoint, p2: MeasurementPoint, p3: MeasurementPoint): number {
    const angle1 = Math.atan2(p1.y - p2.y, p1.x - p2.x);
    const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
    let angle = Math.abs((angle1 - angle2) * (180 / Math.PI));
    if (angle > 180) angle = 360 - angle;
    return Math.round(angle);
  }
}
